/**
 * buildings.ts — The houses.
 *
 * ## A plot is not a house
 *
 * The entries in `BUILDINGS.LIST` are **collision rects** — blocks of the town.
 * Drawing one box per entry is what made the place read as an office park:
 * eight identical extrusions, each as wide as a whole block, each with a single
 * front door and an even grid of windows up it.
 *
 * A market town is built one narrow house at a time. So each plot is cut across
 * its frontage into one to three **cottages** (`systems/terrace.ts`), each with
 * its own height, paint, roof, door and windows. The bays tile the plot exactly,
 * so the town is solid in precisely the same places — this is a change to what
 * gets drawn, never to where you can walk.
 *
 * A cottage is built in the order a real one goes up:
 *
 * ```
 *   plinth → painted ground floor → jettied upper storey        addShell
 *   fascia → slopes → ridge rolls → gable ends → rooflights
 *          → chimney                                            addRoof
 *   arched door, lamps, step                                    addDoor
 *   surrounds, glazing bars, shutters, window boxes             buildFacades
 * ```
 *
 * ## Three things the camera decided
 *
 * All of them come from `CAMERA.TILT_DEG` looking down at 58°:
 *
 * 1. **Every ridge runs front-to-back.** A slope facing the camera at anywhere
 *    near 32° renders as one enormous featureless quad, which is what used to
 *    hold `ROOF_PITCH` down at a flat 0.22. Turning the ridge through a right
 *    angle removes the constraint instead of ducking it: both slopes are then
 *    seen edge-on across the view, where a steep pitch reads *better*. That's
 *    what lets the roofs be 51° and carry as much height as the walls — the
 *    single biggest difference between a cottage and a block. It falls out of
 *    the proportions: a cottage deeper than it is wide gets its ridge that way
 *    round for free.
 * 2. **Anything tall is drawn up-screen from its own footprint**, about 0.63×
 *    its height. That rules out a porch canopy over the front door — at any
 *    depth worth having, the hood lands on top of the doorway it's supposed to
 *    shelter. The door gets an arched head and carriage lamps instead, both
 *    flat against the wall. It's also why the jetty is small: it hangs past the
 *    collision rect, and a big one would be drawn out over the pavement.
 * 3. **The roof is still the biggest thing on any house**, so the roofline gets
 *    the most detail: fascia, rolled ridge tiles, barge boards, a gable end
 *    with an attic roundel, and rooflights let into the slopes.
 *
 * ## Triangle budget
 *
 * The per-window detail is instanced — see `Instances` below — and kept
 * deliberately cheap, because there are a couple of hundred windows in town and
 * they're the most numerous meshes in the game by an order of magnitude. A
 * surround is one chamfered box, and the glazing bars are flat planes; a full
 * rounded frame of six pieces per window costs more than the whole rest of the
 * town put together. Shutters and window boxes go on the **front** face only,
 * which is both cheaper and better-looking: the street elevation is the dressed
 * one and the side walls stay calm.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BUILDINGS, OBJECTIVE } from '../config/constants';
import { activeFootways, activeLevel } from '../config/level';
import { DOORS, MASONRY, ROOFS } from '../config/palette';
import { gableSlope, hipFaces } from '../systems/coursing';
import { ridgeTiles, hipArrises, slopeSeat, slopeNormal } from '../systems/roofline';
import { layOutTown, type Cottage, type HouseSpec } from '../systems/terrace';
import { ROAD_CORRIDOR } from '../systems/levelgen/town';
import {
  frontFacing,
  facadeColumns,
  patioRect,
  rectCenter,
  type Facing,
  type Rect,
} from '../systems/level';
import { shadeHex } from '../utils/color';
import { Instances } from './instances';
import {
  MASONRY_CELL,
  brickTexture,
  courseGeometry,
  hipGeometry,
  masonryMaterial,
  tileTexture,
} from './masonry';
import {
  applyBoundsGreyscale,
  clayBox,
  clayExtrude,
  clayGeometry,
  clayMaterial,
  clayPill,
  claySlab,
  claySphereGeometry,
  clayWobble,
} from './clay';
import { createDoormatWithBone } from './models';

type Building = HouseSpec;

/**
 * Thickness of a gable's sloping slabs. They're centred on the plane of the
 * roof, so their visible surface is half of this above it — which is what
 * anything laid *on* a gable slope has to clear.
 */
const ROOF_SLAB = 3;

/** How far a roof oversails its walls on each side. Zero at a party wall. */
interface Overhang {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

const FULL_OVERHANG: Overhang = {
  x0: BUILDINGS.ROOF_OVERHANG,
  x1: BUILDINGS.ROOF_OVERHANG,
  z0: BUILDINGS.ROOF_OVERHANG,
  z1: BUILDINGS.ROOF_OVERHANG,
};

/** Outward normal of a face, and the yaw that turns +z to point along it. */
const FACES = [
  { nx: 0, nz: 1, ry: 0, facing: 'south' },
  { nx: 0, nz: -1, ry: Math.PI, facing: 'north' },
  { nx: 1, nz: 0, ry: Math.PI / 2, facing: 'east' },
  { nx: -1, nz: 0, ry: -Math.PI / 2, facing: 'west' },
] as const;

const yawOf = (facing: Facing): number => FACES.find((f) => f.facing === facing)!.ry;

/**
 * Whether this face of a cottage is a party wall — i.e. buried in the house
 * next door.
 *
 * Read off the roof overhang rather than tracked separately: the overhang is
 * zeroed at exactly the sides that have a neighbour, so the two can't disagree.
 */
function isPartyWall(c: Cottage, face: (typeof FACES)[number]): boolean {
  if (face.nx > 0) return c.over.x1 === 0;
  if (face.nx < 0) return c.over.x0 === 0;
  if (face.nz > 0) return c.over.z1 === 0;
  return c.over.z0 === 0;
}


const UP = new THREE.Vector3(0, 1, 0);
/** Scratch vectors reused per call rather than allocated in the placement loops. */
const SCRATCH = { p: new THREE.Vector3(), v: new THREE.Vector3() };

/**
 * Cut every plot in the town into cottages, and place them.
 *
 * Two passes on purpose: the structure first, then one facade pass over all of
 * them together, because the windows are instanced across the whole town rather
 * than per building.
 */
export function buildBuildings(scene: THREE.Scene): void {
  const inst = new Instances();
  const level = activeLevel();

  // Two towns, built two ways. Everything inside the walk gets the full
  // cottage — shell, roof, eaves, chimney, door, facade. Everything out in the
  // surround gets `addDistantCottage`, which is a box and a wedge. See there
  // for why the difference is this stark.
  const near: Cottage[] = [];
  for (const c of layOutTown()) {
    if (worthDetailing(c.spec, level.bounds)) near.push(c);
    else addDistantCottage(inst, c);
  }

  for (const c of near) {
    addShell(scene, c.spec, c.cx, c.cz);
    addRoof(scene, inst, c.spec, c.cx, c.cz, c.over);
    addDoor(scene, c.spec, c.cx, c.cz, c.front, c.plan.door);
  }
  const footways = activeFootways();
  for (const plot of level.buildings) {
    if (plot.patio && worthDetailing(plot, level.bounds)) {
      addPatio(scene, plot, frontFacing(plot, footways));
    }
  }

  buildFacades(inst, near);
  inst.build(scene);
}

/**
 * How far past the walk a house still gets built properly.
 *
 * One street and one plot's depth. That is not a fudge factor — it is exactly
 * the row of houses fronting the perimeter street *from the outside*, which the
 * player walks along at arm's length in clear air. They are the closest
 * buildings in the surround, the only ones seen from the front, and the ones a
 * box-and-wedge version would give away instantly. Everything past them is
 * across a street and into the haze.
 */
const DETAIL_MARGIN = ROAD_CORRIDOR + BUILDINGS.BAY_MIN_WIDTH;

/** Whether a building is close enough to the walk to be worth building properly. */
function worthDetailing(plot: Rect, bounds: Rect): boolean {
  return (
    plot.x < bounds.x + bounds.w + DETAIL_MARGIN &&
    plot.x + plot.w > bounds.x - DETAIL_MARGIN &&
    plot.y < bounds.y + bounds.h + DETAIL_MARGIN &&
    plot.y + plot.h > bounds.y - DETAIL_MARGIN
  );
}

/**
 * A cottage in the surround: one box, one wedge, both instanced.
 *
 * ## Why the surround gets its own builder rather than a trimmed-down cottage
 *
 * A full cottage is around two hundred meshes — shell, plinth, eaves band,
 * barge boards, gable slabs, ridge tiles, chimney and pots, door, frame, step,
 * and a window surround per opening. That is the right price for a house the
 * player walks past. It is the wrong price for the surround, which is *five to
 * six times as many houses as the town itself*: built the same way, a generated
 * medium level came out at 32,000 meshes against the hand-authored town's
 * 1,563, and no amount of trimming individual details closes a gap that size.
 *
 * So a distant cottage is drawn as what it actually reads as through the fog —
 * a coloured mass with a pitched top — and goes through `Instances`, so the
 * entire ring costs one draw call per wall colour plus one per roof colour
 * rather than one per house. The geometry is a unit box and a unit prism scaled
 * per instance; both are cached, and both materials come from the same small
 * palette the near town uses, so the batch count stays bounded however many
 * houses are out there.
 *
 * The pitch is `BUILDINGS.ROOF_PITCH` and the ridge runs north–south, exactly
 * as on a real cottage — the silhouette is the whole point, and a distant roof
 * ridged the wrong way would show from the air even when nothing else does.
 */
function addDistantCottage(inst: Instances, c: Cottage): void {
  // The cottage's own colours, the same small palette the near town is painted
  // from. These were a flat out-of-bounds grey for a while; taking the colour out
  // is the bounds filter's job now (clay.ts → `greyOutsideBounds`), so the ring is
  // built in full colour and desaturated where it falls outside the walk.
  const { w, h, tall, color, roofColor } = c.spec;

  inst.placeScaled(`far-wall-${color}`, unitBox(), clayMaterial(color), c.cx, tall / 2, c.cz, 0, w, tall, h);

  // Same rule the real roof follows. `ROOF_PITCH` is a fraction of the smaller
  // footprint dimension and gives a slope of `atan(2 × ROOF_PITCH)`, so the
  // ridge stands `ROOF_PITCH × min(w, h)` above the eaves — the half-span times
  // the tangent. Doubling that (the obvious misreading) puts a roof on every
  // house in the ring that is twice as tall as its walls, which from the
  // boundary reads as a row of tents.
  const rise = Math.min(w, h) * BUILDINGS.ROOF_PITCH;

  // The ridge always runs north–south: every bay in the town is deeper than it
  // is wide by `BAY_MIN_RATIO` — the rule `gableSlope` follows and the
  // validator re-checks as `ridge-across-camera` — so the prism never needs
  // turning.
  inst.placeScaled(
    `far-roof-${roofColor}`,
    unitGable(),
    clayMaterial(roofColor),
    c.cx,
    tall,
    c.cz,
    0,
    w + BUILDINGS.ROOF_OVERHANG * 2,
    rise,
    h + BUILDINGS.ROOF_OVERHANG * 2,
  );

  addDistantWindows(inst, c);
}

/**
 * Windows on a distant cottage: flat panes, instanced, no surrounds.
 *
 * These are what stop the surround reading as grey blocks, and they are close
 * to free — every pane in the ring is one instance of one plane in one batch.
 * That balance is the whole reason the crude cottage is viable: almost nothing
 * a near cottage is made of survives the distance, but **glazing does**. A wall
 * with windows in it is a house; the same wall without them is a shipping
 * container, however correct its roof.
 *
 * Only the south and side faces are glazed. The camera looks north and down, so
 * a north elevation is never in view — see the note about the camera tilt at the
 * top of this file.
 *
 * The grid is regular, and deliberately so. The near town's facades are placed
 * by `facadeColumns` off each cottage's real width, which is what stops a
 * terrace looking stamped out; at this distance nobody can count the windows,
 * and matching that machinery would mean paying for it.
 */
function addDistantWindows(inst: Instances, c: Cottage): void {
  const { w, h, tall } = c.spec;
  const glass = glassMaterial(false);
  const pane = unitPane();
  const key = 'far-window';

  // Two storeys where there is room for two, one where there isn't — the same
  // split the real facade makes around `SHOPFRONT_HEIGHT`.
  const rows =
    tall >= BUILDINGS.SHOPFRONT_HEIGHT + BUILDINGS.STOREY_HEIGHT
      ? [
          BUILDINGS.SHOPFRONT_HEIGHT * 0.55,
          BUILDINGS.SHOPFRONT_HEIGHT + BUILDINGS.STOREY_HEIGHT * 0.5,
        ]
      : [tall * 0.5];

  const across = (extent: number): number[] => {
    const n = Math.max(1, Math.floor(extent / BUILDINGS.WINDOW_SPACING));
    return Array.from({ length: n }, (_, i) => ((i + 0.5) / n - 0.5) * extent);
  };

  // Just proud of the wall, or the pane z-fights with the face it sits on.
  const proud = 0.6;
  for (const y of rows) {
    for (const dx of across(w)) {
      inst.placeScaled(
        key, pane, glass,
        c.cx + dx, y, c.cz + h / 2 + proud, 0,
        BUILDINGS.WINDOW_W, BUILDINGS.WINDOW_H, 1,
      );
    }
    for (const dz of across(h)) {
      for (const side of [-1, 1] as const) {
        inst.placeScaled(
          key, pane, glass,
          c.cx + (side * w) / 2 + side * proud, y, c.cz + dz, Math.PI / 2,
          BUILDINGS.WINDOW_W, BUILDINGS.WINDOW_H, 1,
        );
      }
    }
  }
}

let paneGeo: THREE.BufferGeometry | null = null;
/** A 1×1 upright plane, shared by every distant window and scaled per instance. */
function unitPane(): THREE.BufferGeometry {
  paneGeo ??= new THREE.PlaneGeometry(1, 1);
  return paneGeo;
}

let boxGeo: THREE.BufferGeometry | null = null;
/** A 1×1×1 box, shared by every distant wall and scaled per instance. */
function unitBox(): THREE.BufferGeometry {
  boxGeo ??= new THREE.BoxGeometry(1, 1, 1);
  return boxGeo;
}

let gableGeo: THREE.BufferGeometry | null = null;
/**
 * A unit gable prism: a triangle one wide and one high, extruded one deep, with
 * its ridge running along **z** and its base sitting on y = 0.
 *
 * Built by hand rather than extruded from a `THREE.Shape`, because an extrusion
 * carries bevel and UV machinery this has no use for — and this geometry is
 * shared by every house in the ring, so it is worth the eighteen vertices being
 * exactly the eighteen that are needed.
 */
function unitGable(): THREE.BufferGeometry {
  if (gableGeo) return gableGeo;
  const geo = new THREE.BufferGeometry();
  // Two slopes, two gable ends. No underside: it is never seen, and leaving it
  // out is two fewer triangles on every house in the surround.
  const v = (x: number, y: number, z: number): number[] => [x, y, z];
  const positions = [
    // West slope (from the eaves up to the ridge), south to north.
    ...v(-0.5, 0, 0.5), ...v(0, 1, 0.5), ...v(0, 1, -0.5),
    ...v(-0.5, 0, 0.5), ...v(0, 1, -0.5), ...v(-0.5, 0, -0.5),
    // East slope.
    ...v(0.5, 0, 0.5), ...v(0.5, 0, -0.5), ...v(0, 1, -0.5),
    ...v(0.5, 0, 0.5), ...v(0, 1, -0.5), ...v(0, 1, 0.5),
    // The two gable ends.
    ...v(-0.5, 0, 0.5), ...v(0.5, 0, 0.5), ...v(0, 1, 0.5),
    ...v(-0.5, 0, -0.5), ...v(0, 1, -0.5), ...v(0.5, 0, -0.5),
  ];
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  gableGeo = geo;
  return geo;
}

/**
 * The walker's own cottage, built from the same parts as the town.
 *
 * It's the thing the player sets off from and is walking *back toward* for the
 * last leg of the run, so it was the worst place in the game to have a box with
 * a cone on it — which is exactly what it was. Deliberately a cottage rather
 * than a shop: no shopfront band to speak of, a gable end turned to face the
 * way you approach from, and its windows placed by hand rather than by the
 * facade pass, which only knows about buildings in `BUILDINGS.LIST`.
 *
 * `plot` is its footprint — the same rect the walker collides with, worked out
 * once in `systems/home.ts` rather than twice. Walls drawn anywhere but on the
 * rect that stops you are walls you can stand inside.
 */
export function buildCottage(parent: THREE.Object3D, plot: Rect): void {
  const [x, z] = rectCenter(plot);
  const spec: HouseSpec = {
    // Deeper than it is wide, so the ridge runs north–south and a gable end
    // — the one with the round attic window in it — faces the approach.
    x: plot.x,
    y: plot.y,
    w: plot.w,
    h: plot.h,
    tall: 44,
    color: OBJECTIVE.COTTAGE.WALL,
    roof: 'gable',
    roofColor: OBJECTIVE.COTTAGE.ROOF,
    paint: OBJECTIVE.COTTAGE.PAINT,
    material: 'render',
  };
  const inst = new Instances();

  addShell(parent, spec, x, z, OBJECTIVE.COTTAGE.BAND);
  addRoof(parent, inst, spec, x, z);
  addDoor(parent, spec, x, z, 'south');

  const doormat = createDoormatWithBone();
  doormat.position.set(x, 0.2, z + spec.h / 2 + 7.5);
  parent.add(doormat);

  // A window each side of the door, and one on each flank.
  const { WINDOW_W: ww, WINDOW_H: wh } = BUILDINGS;
  for (const side of [-1, 1]) {
    addWindow(parent, {
      x: x + side * 18,
      y: 30,
      z: z + spec.h / 2,
      ry: 0,
      w: ww,
      h: wh,
      lit: true,
    });
    addWindow(parent, {
      x: x + (side * spec.w) / 2,
      y: 30,
      z,
      ry: side * (Math.PI / 2),
      w: ww,
      h: wh,
      lit: side > 0,
    });
  }

  inst.build(parent);
}

// ─────────────────────────────────────────────────────────────────────────────
// The shell
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A cottage's walls: a stone plinth, a painted ground floor, and an upper
 * storey **jettied** out over it.
 *
 * The jetty is what stops a house being an extrusion. A single box from
 * pavement to eaves has one unbroken vertical silhouette however it's painted;
 * oversailing the upper floor by a couple of units puts a real horizontal break
 * in it, with a shadow under the overhang that this camera catches down the
 * whole length of the terrace. It's also why the two storeys are separate
 * boxes rather than one — the brickwork belongs on the upper floor, which is
 * the part that's actually brick.
 *
 * It oversails **front and back only**. Sideways is where the party walls are,
 * and a jetty there would push into the cottage next door.
 *
 * Everything else is proud of the wall by a unit or two for the same reason —
 * a band painted flush reads as a stripe, not as masonry.
 */
function addShell(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  bandHeight: number = BUILDINGS.SHOPFRONT_HEIGHT,
): void {
  const brick = b.material === 'brick';
  const ground = Math.min(bandHeight, b.tall);
  const upper = Math.max(b.tall - ground, 0);
  // The jetty runs across the front, so it grows whichever axis the front face
  // lies on. A cottage is always deeper than it is wide (see terrace.ts), so
  // that's the long axis.
  const jetty = upper > 0 ? BUILDINGS.JETTY : 0;
  const deep = b.h >= b.w;
  const upperW = b.w + (deep ? 0 : jetty * 2);
  const upperD = b.h + (deep ? jetty * 2 : 0);

  // Stone base course. Kept shorter than the doorstep in front of it, or the
  // step stops reading as a step and becomes part of the base.
  const { PLINTH_HEIGHT: ph, PLINTH_PROUD: pp } = BUILDINGS;
  scene.add(clayBox(b.w + pp * 2, ph, b.h + pp * 2, BUILDINGS.PLINTH_COLOR, cx, ph / 2, cz));

  // Painted ground floor — the shopfront, and the storey the front door is in.
  const shop = clayBox(b.w, ground, b.h, b.paint, cx, ground / 2, cz);
  shop.receiveShadow = true;
  scene.add(shop);

  if (upper > 0) {
    // The jettied storey. A brick cottage carries its brickwork as a texture on
    // this shell, so the pattern wraps the rounded corners instead of stopping
    // short of them, and the windows sit on top of it.
    const walls = new THREE.Mesh(
      brick
        ? courseGeometry(upperW, upper, upperD, ...MASONRY_CELL)
        : clayGeometry(upperW, upper, upperD),
      brick
        ? masonryMaterial(b.color, brickTexture(b.color), BUILDINGS.BRICK_RELIEF)
        : clayMaterial(b.color),
    );
    walls.position.set(cx, ground + upper / 2, cz);
    walls.castShadow = true;
    walls.receiveShadow = true;
    // A fraction of a degree off true, so the terrace looks pressed out by hand
    // rather than extruded on a grid. Rotation only — the collision rect is
    // still the axis-aligned footprint from BUILDINGS.LIST.
    clayWobble(walls, cx, cz);
    scene.add(walls);

    // The bressummer: the beam the jetty lands on, a shade up from the paint so
    // it reads as joinery catching the light rather than a second stripe.
    const { STRING_COURSE_HEIGHT: sh, STRING_COURSE_PROUD: sp } = BUILDINGS;
    scene.add(
      clayBox(
        upperW + sp * 2,
        sh,
        upperD + sp * 2,
        shadeHex(b.paint, 0.28),
        cx,
        ground + sh / 2,
        cz,
      ),
    );
  }
}

/*
 * There is no drainpipe any more, and it's worth saying why so nobody adds one
 * back. A downpipe is a vertical element standing at a corner of the walls, and
 * at this tilt anything vertical is drawn about 0.63× its height *up the
 * screen* — which, on a cottage whose roof is as tall as its walls, puts a pale
 * stripe straight up the middle of its own tiles. It read fine on the old flat-
 * roofed blocks because there was barely any roof for it to cross. The fascia
 * board along the eaves does the same job now: it's what a gutter looks like
 * from above.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The roofline
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cap a building — the single biggest cue that this is a town rather than an
 * office park, and the largest surface the camera ever sees.
 *
 * The slopes are as they were: a **hip** is four tiled triangles rising to a
 * shared apex, a **gable** is two slabs leaning together over the longer axis
 * (it can't be a scaled pyramid — that still comes to a point). What's new is
 * everything at their edges, in the order it's built:
 *
 * - a **fascia** band on the wall top, which the slopes then sit on. This is
 *   what gives the roof a thickness; without it the tiles taper into the wall
 *   and the roof looks like a lid resting on a box.
 * - **rolled tiles** along every edge two slopes meet at — the ridge of a
 *   gable, the four sloping corners of a hip. They hide a seam and, more
 *   importantly, put a lit line along the roof's spine, which is the only thing
 *   a slope facing near-square at the camera has for the eye to catch.
 * - **gable ends**: a triangle of wall closing off the ridge, with barge boards
 *   down its slopes and a round attic window in the middle.
 * - **dormers**, one or two, on the slope facing the street.
 */
function addRoof(
  scene: THREE.Object3D,
  inst: Instances,
  b: Building,
  cx: number,
  cz: number,
  over: Overhang = FULL_OVERHANG,
): void {
  const w = b.w + over.x0 + over.x1;
  const d = b.h + over.z0 + over.z1;
  // With no overhang at a party wall the roof isn't centred on its own walls.
  const rx = cx + (over.x1 - over.x0) / 2;
  const rz = cz + (over.z1 - over.z0) / 2;
  const height = Math.min(w, d) * BUILDINGS.ROOF_PITCH;
  // The eaves band stands on the walls, and everything above starts on top of it.
  const base = b.tall + BUILDINGS.EAVES_HEIGHT;
  // Tiling is part of the roof's surface, so it comes with the material rather
  // than being laid over the finished roof as separate pieces.
  const mat = masonryMaterial(b.roofColor, tileTexture(b.roofColor), BUILDINGS.TILE_RELIEF);
  const rollColor = shadeHex(b.roofColor, ROOFS.RIDGE_LIGHTEN);

  addEaves(scene, b, rx, rz, w, d);

  if (b.roof === 'gable') {
    const { alongX, ridge: ridgeLen, span, slant, tilt } = gableSlope(w, d, height);

    for (const side of [-1, 1]) {
      // The slab is always modelled `ridgeLen` × `slant` and turned to suit, so
      // the courses follow the ridge whichever way it runs. The `swap` flag
      // turns the tiling through the same right angle the slab is turned.
      const plane = new THREE.Mesh(
        alongX
          ? courseGeometry(ridgeLen, ROOF_SLAB, slant, ...MASONRY_CELL)
          : courseGeometry(slant, ROOF_SLAB, ridgeLen, ...MASONRY_CELL, true),
        mat,
      );
      // Each slab's centre sits halfway between ridge and eave, so it spans from
      // the ridge (span/2 in, at full height) down to the eave (at wall height).
      const across = (side * span) / 4;
      plane.position.set(rx + (alongX ? 0 : across), base + height / 2, rz + (alongX ? across : 0));
      // Tilt so the slab's long axis runs ridge → eave. The signs are opposite
      // between the two axes because rotating +Z about X and +X about Z sweep in
      // opposite directions; getting these backwards splays the roof outward
      // instead of closing it into a ridge.
      if (alongX) plane.rotation.x = side * tilt;
      else plane.rotation.z = -side * tilt;
      plane.castShadow = true;
      plane.receiveShadow = true;
      scene.add(plane);

      addBargeBoards(scene, b, rx, rz, { alongX, ridgeLen, across, slant, tilt, side, base, height });
    }

    // A rolled clay sausage along the ridge, capped with half-round tiles at the
    // same rhythm as the courses below them.
    // Lifted by half the slab thickness: the slabs are centred on the roof
    // plane, so a roll seated on the plane itself sinks into the tiles and only
    // its very top shows.
    const ridgeY = base + height + ROOF_SLAB / 2;
    const ridge = clayPill(BUILDINGS.RIDGE_RADIUS, ridgeLen, rollColor);
    ridge.position.set(rx, ridgeY, rz);
    // Capsules stand up the y axis; lay it along whichever way the ridge runs.
    if (alongX) ridge.rotation.z = Math.PI / 2;
    else ridge.rotation.x = Math.PI / 2;
    scene.add(ridge);

    const tileGeo = claySphereGeometry(BUILDINGS.RIDGE_TILE_RADIUS, 0.7);
    const tileMat = clayMaterial(rollColor);
    for (const t of ridgeTiles(ridgeLen, BUILDINGS.RIDGE_TILE_SPACING)) {
      const x = rx + (alongX ? t : 0);
      const z = rz + (alongX ? 0 : t);
      inst.place(`ridge:${rollColor}`, tileGeo, tileMat, x, ridgeY, z);
    }

    for (const side of [-1, 1]) {
      // Placed off the *wall*, not the roof: the gable end closes the storeys
      // below it, and the eaves overhang it exactly as they overhang the wall.
      addGableEnd(scene, b, alongX ? rx : cx, alongX ? cz : rz, {
        alongX,
        span,
        height,
        base,
        side,
      });
    }
    // Both slopes: a gable shows one of them to the camera and the other to
    // whoever is standing on the far pavement.
    addRooflights(
      scene,
      inst,
      b,
      rx,
      rz,
      base,
      [1, -1].map((side) => ({
        ox: alongX ? 0 : side,
        oz: alongX ? side : 0,
        reach: span / 2,
        rise: height,
        eave: ridgeLen,
        lift: ROOF_SLAB / 2,
        narrows: false,
      })),
    );
  } else {
    // Four tiled triangles rather than a squashed cone: a cone's own UVs
    // parameterise the whole pyramid at once, which stretches the tiles across
    // the long slopes of an oblong house. See systems/coursing.ts.
    const roof = new THREE.Mesh(hipGeometry(w, d, height, ...MASONRY_CELL), mat);
    roof.position.set(rx, base, rz);
    roof.castShadow = true;
    roof.receiveShadow = true;
    scene.add(roof);

    // Rolled tiles down each corner where two slopes meet, and a ball capping
    // the point they all run into.
    for (const a of hipArrises(w, d, height)) {
      const roll = clayPill(BUILDINGS.HIP_ROLL_RADIUS, a.length, rollColor);
      roll.position.set(rx + a.mid[0], base + a.mid[1], rz + a.mid[2]);
      // Capsules stand up y; turn that axis onto the run of the arris.
      roll.quaternion.setFromUnitVectors(UP, SCRATCH.v.set(...a.dir).normalize());
      scene.add(roll);
    }
    // A ball capping the point all four arrises run into — left bare, a hip
    // ends in a spike you can see the seams of from directly above.
    const apex = new THREE.Mesh(
      claySphereGeometry(BUILDINGS.RIDGE_TILE_RADIUS),
      clayMaterial(rollColor),
    );
    apex.position.set(rx, base + height, rz);
    apex.castShadow = true;
    scene.add(apex);

    // All four slopes of a hip, which between them face every direction the
    // camera can be pointing.
    addRooflights(
      scene,
      inst,
      b,
      rx,
      rz,
      base,
      hipFaces(w, d, height, ...MASONRY_CELL).map((f) => ({
        ox: f.ox,
        oz: f.oz,
        reach: f.reach,
        rise: height,
        eave: f.halfEave * 2,
        lift: 0,
        narrows: true,
      })),
    );
  }

  addChimney(scene, b, cx, cz, height);
}

/**
 * The fascia: a board right round the building at the roof's own overhanging
 * footprint, with a moulded lip along the bottom of it.
 *
 * Sized off the roof rather than the wall, so it *is* the overhang — the tiles
 * land on it and the eaves throw a shadow line onto the wall below.
 */
function addEaves(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  w: number,
  d: number,
): void {
  const { EAVES_HEIGHT: h, EAVES_LIP: lip } = BUILDINGS;
  scene.add(
    clayBox(w, h, d, shadeHex(b.roofColor, ROOFS.EAVES_DARKEN), cx, b.tall + h / 2, cz),
  );
  scene.add(
    clayBox(
      w + lip * 2,
      h * 0.34,
      d + lip * 2,
      shadeHex(b.roofColor, ROOFS.EAVES_DARKEN / 2),
      cx,
      b.tall + h * 0.17,
      cz,
    ),
  );
}

/** Geometry shared between a gable's slope and the trim that follows it. */
interface GableSide {
  alongX: boolean;
  ridgeLen: number;
  across: number;
  slant: number;
  tilt: number;
  side: number;
  base: number;
  height: number;
}

/**
 * A barge board capping the tile ends at each end of a gable slope.
 *
 * Takes the same transform as the slope it follows, shifted out along the ridge
 * to the very end of it. Deeper than the slab is thick, so it stands a little
 * proud of the tiles — which is what a real barge board does, and what stops the
 * end of the roof looking sliced off.
 */
function addBargeBoards(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  g: GableSide,
): void {
  const { BARGE_WIDTH: bw, BARGE_HEIGHT: bh } = BUILDINGS;
  const color = shadeHex(b.roofColor, ROOFS.EAVES_DARKEN);
  // Half the ridge, less half the board, puts the board's outer face flush with
  // the end of the slope.
  const out = g.ridgeLen / 2 - bw / 2;

  for (const end of [-1, 1]) {
    const board = new THREE.Mesh(
      g.alongX ? clayGeometry(bw, bh, g.slant) : clayGeometry(g.slant, bh, bw),
      clayMaterial(color),
    );
    board.position.set(
      cx + (g.alongX ? end * out : g.across),
      g.base + g.height / 2,
      cz + (g.alongX ? g.across : end * out),
    );
    // The rotation axis runs along the ridge, so sliding the board out along it
    // afterwards doesn't disturb the tilt.
    if (g.alongX) board.rotation.x = g.side * g.tilt;
    else board.rotation.z = -g.side * g.tilt;
    board.castShadow = true;
    scene.add(board);
  }
}

/**
 * The triangle of wall closing off the end of a ridged roof, with a round
 * attic window in it.
 *
 * Without this a gable is two slabs with a hole between them, which you can see
 * straight through from the side. Filling it also hands the town its one piece
 * of ornament that isn't a rectangle: a roundel with a cross of glazing bars,
 * sitting where a real cottage puts its loft light.
 */
function addGableEnd(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  g: { alongX: boolean; span: number; height: number; base: number; side: number },
): void {
  const half = g.span / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-half, 0);
  shape.lineTo(half, 0);
  shape.lineTo(0, g.height);
  shape.closePath();

  const t = BUILDINGS.GABLE_THICKNESS;
  // A brick wall's *texture* lightens the brick faces off the base colour, so
  // its average tone sits well above `b.color`. The gable is flat clay with no
  // pattern on it, and left at the base colour it reads as a dark panel let
  // into the wall it's supposed to continue.
  const tone =
    b.material === 'brick' ? shadeHex(b.color, MASONRY.BRICK_FACE_LIGHTEN * 0.6) : b.color;
  const wall = clayExtrude(shape, t, tone);
  // Set flush with the wall below, so the roof overhangs it exactly as it
  // overhangs the storeys underneath.
  const outer = (g.alongX ? b.w : b.h) / 2;
  const ry = g.alongX ? g.side * (Math.PI / 2) : g.side === 1 ? 0 : Math.PI;
  wall.rotation.y = ry;
  wall.position.set(
    cx + (g.alongX ? g.side * (outer - t) : 0),
    g.base,
    cz + (g.alongX ? 0 : g.side * (outer - t)),
  );
  scene.add(wall);

  addRoundel(scene, {
    x: cx + (g.alongX ? g.side * outer : 0),
    y: g.base + g.height * 0.32,
    z: cz + (g.alongX ? 0 : g.side * outer),
    ry,
  });
}

/** A round attic window: a ring of frame, a pane, and a cross of glazing bars. */
function addRoundel(scene: THREE.Object3D, at: { x: number; y: number; z: number; ry: number }): void {
  const { ROUNDEL_RADIUS: r, ROUNDEL_FRAME: f } = BUILDINGS;
  const group = new THREE.Group();

  const pane = new THREE.Mesh(new THREE.CircleGeometry(r, 20), glassMaterial(false));
  pane.position.z = 0.6;
  group.add(pane);

  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(r, f / 2, 6, 20),
    clayMaterial(BUILDINGS.WINDOW_SILL_COLOR),
  );
  ring.position.z = 0.6;
  ring.castShadow = true;
  group.add(ring);

  const bar = clayGeometry(r * 2, BUILDINGS.GLAZING_BAR, 1.2);
  const barMat = clayMaterial(BUILDINGS.WINDOW_SILL_COLOR);
  for (const turn of [0, Math.PI / 2]) {
    const b = new THREE.Mesh(bar, barMat);
    b.rotation.z = turn;
    b.position.z = 1;
    group.add(b);
  }

  group.rotation.y = at.ry;
  group.position.set(at.x, at.y, at.z);
  scene.add(group);
}

/** One slope of a roof, as much of it as laying something flat on it needs. */
interface Slope {
  /** Which way the slope faces on the ground plane. */
  ox: number;
  oz: number;
  /** Horizontal run from the ridge line out to the eave. */
  reach: number;
  /** Height gained over that run. */
  rise: number;
  /** How long the slope is along its own eave. */
  eave: number;
  /**
   * How far to lift off the mathematical plane of the slope to clear whatever
   * is drawn on it. A gable's slopes are 3-unit-thick slabs centred on that
   * plane, so anything seated at zero is buried inside the tiles; a hip's are
   * zero-thickness triangles and need nothing.
   */
  lift: number;
  /**
   * True for a hip's triangular slopes, which narrow to nothing at the apex.
   * A rooflight placed off-centre on one of those runs off the edge of the
   * triangle and hangs in the air beside the roof — the width available is only
   * the *seated* fraction of the eave, not the eave itself.
   */
  narrows: boolean;
}

/**
 * Rooflights let into a slope: one on a short roof, two on a long one.
 *
 * This is the roof's share of the detail budget, and it's shaped entirely by the
 * camera. Anything standing *up* from a roof this shallow is taller than the
 * roof (see the note on ROOFLIGHT_* in constants), but the same camera that
 * makes height a problem also points straight down at the slope — so glazing
 * lying flat in the tiles is both the cheapest thing to build and the most
 * visible thing up there.
 *
 * Each light is seated on the slope by `slopeSeat` and turned to lie in it by
 * `slopeNormal`, so it stays flush whatever the pitch works out at.
 */
function addRooflights(
  scene: THREE.Object3D,
  inst: Instances,
  b: Building,
  cx: number,
  cz: number,
  base: number,
  slopes: Slope[],
): void {
  const {
    ROOFLIGHT_W: rw,
    ROOFLIGHT_H: rh,
    ROOFLIGHT_UP: up,
    ROOFLIGHT_KERB: kerb,
    ROOFLIGHT_PROUD: proud,
    ROOFLIGHT_PAIR_AT: pairAt,
  } = BUILDINGS;

  const kerbColor = shadeHex(b.roofColor, ROOFS.EAVES_DARKEN);
  const kerbGeo = clayGeometry(rw + kerb * 2, rh + kerb * 2, proud);
  const kerbMat = clayMaterial(kerbColor);
  const paneGeo = new THREE.PlaneGeometry(rw, rh);

  for (const s of slopes) {
    // How much of the eave is still under roof at the height they sit at.
    const run = s.narrows ? s.eave * (1 - up) : s.eave;
    // Too narrow to take one without crowding whatever bounds the slope.
    if (run < rw * 2.4) continue;

    const seat = slopeSeat(s.reach * 2, s.rise, up);
    const n = slopeNormal(s.ox, s.oz, s.reach, s.rise);
    // The eave runs at a right angle to the way the slope faces.
    const ax = s.oz;
    const az = -s.ox;

    const count = run >= pairAt ? 2 : 1;
    const step = run / (count + 1);

    for (let i = 0; i < count; i++) {
      const along = -run / 2 + step * (i + 1);
      const px = cx + s.ox * seat.across + ax * along;
      const py = base + seat.y;
      const pz = cz + s.oz * seat.across + az * along;

      // lookAt turns +z onto the surface normal, which is what both the kerb
      // (extruded along z) and the pane (a plane facing +z) are built around.
      // Off the plane far enough to sit on the tiles rather than in them.
      const kx = px + n[0] * s.lift;
      const ky = py + n[1] * s.lift;
      const kz = pz + n[2] * s.lift;

      SCRATCH.p.set(kx + n[0], ky + n[1], kz + n[2]);
      const seatOn = new THREE.Object3D();
      seatOn.position.set(kx, ky, kz);
      // lookAt turns +z onto the surface normal, which is what both the kerb
      // (extruded along z) and the pane (a plane facing +z) are built around.
      seatOn.lookAt(SCRATCH.p);
      seatOn.updateMatrix();

      inst.add(`rooflight:${kerbColor}`, kerbGeo, kerbMat, seatOn.matrix);

      const out = proud / 2 + 0.4;
      const pane = new THREE.Mesh(paneGeo, rooflightMaterial());
      pane.position.set(kx + n[0] * out, ky + n[1] * out, kz + n[2] * out);
      pane.quaternion.copy(seatOn.quaternion);
      scene.add(pane);
    }
  }
}

/** A brick stack with a corbelled cap and a pot, offset to one side of the ridge. */
function addChimney(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  roofHeight: number,
): void {
  // Measured so the stack always clears the ridge by the same margin, whatever
  // the roof's pitch works out at.
  const stackH = BUILDINGS.EAVES_HEIGHT + roofHeight + BUILDINGS.CHIMNEY_RISE;
  const width = 9;
  const stack = clayBox(width, stackH, width, BUILDINGS.CHIMNEY_COLOR, 0, 0, 0);
  // Sit it a quarter of the way along the building so it isn't dead centre.
  const ox = b.w >= b.h ? b.w * 0.25 : 0;
  const oz = b.w >= b.h ? 0 : b.h * 0.25;
  stack.position.set(cx + ox, b.tall + stackH / 2, cz + oz);
  scene.add(stack);

  // The corbelled cap: a course oversailing the stack, which is what makes a
  // chimney read as brickwork rather than as a post stuck through the roof.
  const { CHIMNEY_CAP_PROUD: cp, CHIMNEY_CAP_HEIGHT: ch } = BUILDINGS;
  const top = b.tall + stackH;
  scene.add(
    clayBox(
      width + cp * 2,
      ch,
      width + cp * 2,
      shadeHex(BUILDINGS.CHIMNEY_COLOR, -0.12),
      cx + ox,
      top - ch / 2,
      cz + oz,
    ),
  );

  // One pot on a narrow stack, a pair on a wide one.
  const pots = width >= BUILDINGS.CHIMNEY_TWIN_AT ? [-2.6, 2.6] : [0];
  for (const off of pots) {
    const pot = new THREE.Mesh(
      new THREE.CylinderGeometry(2.4, 3, 7, 8),
      clayMaterial(BUILDINGS.CHIMNEY_POT_COLOR),
    );
    pot.position.set(
      cx + ox + (b.w >= b.h ? off : 0),
      top + 3,
      cz + oz + (b.w >= b.h ? 0 : off),
    );
    pot.castShadow = true;
    scene.add(pot);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The front door
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An outline with a semicircular head — an arched door, or the architrave
 * around it. Drawn about the origin at its foot, `w` wide and `h` tall overall.
 *
 * The arch eats into the height rather than standing on top of it, which is what
 * keeps the whole opening inside `SHOPFRONT_HEIGHT` and out of the first row of
 * windows.
 */
function archShape(w: number, h: number): THREE.Shape {
  const r = w / 2;
  const straight = Math.max(h - r, r * 0.2);
  const s = new THREE.Shape();
  s.moveTo(-r, 0);
  s.lineTo(-r, straight);
  s.absarc(0, straight, r, Math.PI, 0, true);
  s.lineTo(r, 0);
  s.closePath();
  return s;
}

/**
 * A front door on whichever face looks onto a street.
 *
 * Built as architrave → arched door → planks → knob, each sitting slightly
 * proud of the one behind, with a stone step at its foot and a carriage lamp
 * either side. The door stays darker than every wall and shopfront colour on
 * purpose: put it in the same tone as the painted band it sits in and it
 * disappears.
 *
 * Note what *isn't* here — a porch canopy. See the note at the top of this file:
 * from this camera a hood deep enough to read is drawn straight down over the
 * doorway it shelters.
 */
function addDoor(
  scene: THREE.Object3D,
  b: Building,
  cx: number,
  cz: number,
  facing: Facing,
  offset = 0,
): void {
  const w = BUILDINGS.DOOR_WIDTH;
  const h = BUILDINGS.DOOR_HEIGHT;

  const nx = facing === 'east' ? 1 : facing === 'west' ? -1 : 0;
  const nz = facing === 'south' ? 1 : facing === 'north' ? -1 : 0;
  const wall = nx !== 0 ? b.w / 2 : b.h / 2;
  const alongX = nz !== 0; // the wall runs along x
  const ry = yawOf(facing);

  /** A point on the door's face: `along` the wall, `y` up, `out` from the wall. */
  const at = (along: number, y: number, out: number): [number, number, number] => [
    cx + (alongX ? offset + along : nx * (wall + out)),
    y,
    cz + (alongX ? nz * (wall + out) : offset + along),
  ];
  const put = (mesh: THREE.Object3D, along: number, y: number, out: number): void => {
    mesh.rotation.y = ry;
    mesh.position.set(...at(along, y, out));
    scene.add(mesh);
  };

  // Architrave, a touch larger than the door all round and following its arch.
  put(clayExtrude(archShape(w + 6, h + 4), 1.4, BUILDINGS.DOOR_FRAME_COLOR), 0, 0, 0.6);
  put(clayExtrude(archShape(w, h), 1.4, BUILDINGS.DOOR_COLOR), 0, 0, 1.4);

  // Vertical boards pressed into the door face. Barely a unit wide each, so they
  // land under CLAY.DETAIL_MIN and cost a chamfer rather than a rounded box.
  const planks = BUILDINGS.DOOR_PLANKS;
  const plankColor = shadeHex(BUILDINGS.DOOR_COLOR, DOORS.PANEL_SHADE);
  for (let i = 1; i < planks; i++) {
    const along = (i / planks - 0.5) * w;
    const board = clayBox(1.1, h - w / 2 - 3, 1, plankColor);
    if (!alongX) board.geometry = clayGeometry(1, h - w / 2 - 3, 1.1);
    put(board, along, (h - w / 2 - 3) / 2 + 1.5, 2.4);
  }

  // Knob on the opening side, at hand height.
  const knob = new THREE.Mesh(claySphereGeometry(1.5, 0.7), clayMaterial(BUILDINGS.DOOR_KNOB_COLOR));
  knob.position.set(...at(w * 0.3, h * 0.45, 2.6));
  scene.add(knob);

  // Carriage lamps. They sit flat against the wall beside the opening, which is
  // the one place near a door this camera can't hide something behind itself.
  const lampR = BUILDINGS.DOOR_LAMP_RADIUS;
  for (const side of [-1, 1]) {
    const along = side * (w / 2 + 7);
    const bracket = clayBox(2, 5, 2, BUILDINGS.DOOR_LAMP_COLOR);
    put(bracket, along, h * 0.82 + 4, 1.6);
    const globe = new THREE.Mesh(claySphereGeometry(lampR, 0.7), glassMaterial(true));
    globe.position.set(...at(along, h * 0.82, 2));
    scene.add(globe);
    const hood = clayBox(lampR * 2.2, 1.4, lampR * 2.2, BUILDINGS.DOOR_LAMP_COLOR);
    put(hood, along, h * 0.82 + lampR + 0.7, 2);
  }

  // Stone step, spilling out onto the pavement.
  const step = clayBox(
    alongX ? w + 8 : 7,
    3,
    alongX ? 7 : w + 8,
    BUILDINGS.DOOR_STEP_COLOR,
  );
  step.position.set(...at(0, 1.5, 3));
  step.receiveShadow = true;
  scene.add(step);
}

/**
 * A paved forecourt between the front door and the street, with a kerbed edge
 * and a path up to the door.
 *
 * The building has already been pulled back by `PATIO_DEPTH` in the config to
 * leave this room. Nothing here has collision: it's all at footway-kerb height,
 * which the player already steps over freely.
 */
function addPatio(scene: THREE.Object3D, b: Rect, front: Facing): void {
  const { PATIO_DEPTH: depth, PATIO_INSET: inset, PATIO_HEIGHT: h } = BUILDINGS;
  const alongX = front === 'north' || front === 'south'; // patio runs across the door's face

  // Footprint from the shared helper, so the gardens in `yards.ts` can keep off
  // exactly the ground this covers.
  const pad = patioRect(b, front, depth, inset);
  const [px, pz] = rectCenter(pad);
  const sizeX = pad.w;
  const sizeZ = pad.h;

  // Kerbed slab: a low block with paving laid on top, exactly how the footways
  // are built, so a patio reads as the same kind of surface.
  const kerb = clayBox(sizeX, h, sizeZ, BUILDINGS.PATIO_EDGE_COLOR, px, h / 2, pz);
  kerb.castShadow = false;
  scene.add(kerb);

  const e = BUILDINGS.PATIO_EDGE_WIDTH;
  scene.add(claySlab(sizeX - e * 2, sizeZ - e * 2, BUILDINGS.PATIO_COLOR, px, h + 0.1, pz));

  // Path from the gate to the door, in a warmer stone.
  const pw = BUILDINGS.PATIO_PATH_WIDTH;
  scene.add(
    claySlab(
      alongX ? pw : depth,
      alongX ? depth : pw,
      BUILDINGS.PATIO_PATH_COLOR,
      px,
      h + 0.2,
      pz,
    ),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Windows
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Glass is the one thing in town that ISN'T clay — a window that reads as a
 * matte pressed-in square looks like a hole. Keeping a little gloss on it (low
 * roughness, no subsurface glow) is what makes it read as glazing set into the
 * clay rather than another lump of it.
 *
 * There's a little emissive even when "unlit": with no environment map in the
 * scene a low-roughness surface has nothing to reflect, and an unlit pane
 * renders as a black slot punched in the wall.
 */
const glassCache = new Map<boolean, THREE.MeshStandardMaterial>();

function glassMaterial(lit: boolean): THREE.MeshStandardMaterial {
  let m = glassCache.get(lit);
  if (!m) {
    const color = lit ? BUILDINGS.WINDOW_COLOR : BUILDINGS.WINDOW_DARK_COLOR;
    m = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: lit ? 0.4 : 0.25,
      roughness: lit ? 0.25 : 0.3,
      metalness: 0,
      side: THREE.DoubleSide,
    });
    applyBoundsGreyscale(m);
    glassCache.set(lit, m);
  }
  return m;
}

/**
 * Glazing laid flat in a roof, reflecting the sky.
 *
 * Neither of the wall glasses works up here. The lit one glows warm and reads
 * as a hole punched in the tiles from this near-overhead camera, which is
 * exactly the angle that shows the most roof; the unlit one is within a few
 * points of `ROOFS.SLATE` and vanishes on three of the town's four roof
 * colours. A pale sky tone is the one that reads as glass on both slate and
 * terracotta.
 */
let rooflightMat: THREE.MeshStandardMaterial | undefined;

function rooflightMaterial(): THREE.MeshStandardMaterial {
  if (!rooflightMat) {
    rooflightMat = new THREE.MeshStandardMaterial({
      color: BUILDINGS.ROOFLIGHT_COLOR,
      emissive: BUILDINGS.ROOFLIGHT_COLOR,
      emissiveIntensity: 0.18,
      roughness: 0.22,
      metalness: 0,
      side: THREE.DoubleSide,
    });
    applyBoundsGreyscale(rooflightMat);
  }
  return rooflightMat;
}

const frameCache = new Map<string, THREE.BufferGeometry>();

/**
 * Everything around a pane of glass, as one mesh: the stone surround it sits
 * in, the sill under it, and a glazing bar each way across it.
 *
 * Merged into a single geometry because these are the most numerous objects in
 * the town — one instanced batch per pane size draws every window on every
 * building. Three deliberate economies, all of them about that count:
 *
 * - the surround is a **solid block** behind the glass, not a four-sided frame.
 *   A ring of four rails costs four times as much to say the same thing, since
 *   the glass covers the middle of it anyway.
 * - the glazing bars are flat **planes**, not boxes. They're a unit wide; from
 *   the street they're a painted line either way across the pane, and a rounded
 *   box would spend a hundred triangles agreeing.
 * - the sill is the one part that keeps its own depth, because it's the piece
 *   that catches light from above at this camera angle.
 */
function frameGeometry(w: number, h: number): THREE.BufferGeometry {
  const key = `${w}x${h}`;
  const hit = frameCache.get(key);
  if (hit) return hit;

  const {
    WINDOW_SURROUND_PAD: pad,
    WINDOW_SURROUND_DEPTH: depth,
    WINDOW_PROUD: proud,
    GLAZING_BAR: bar,
  } = BUILDINGS;

  const parts: THREE.BufferGeometry[] = [];

  const surround = clayGeometry(w + pad * 2, h + pad * 2, depth).clone();
  parts.push(surround);

  const sill = clayGeometry(w + pad * 2 + 3, 1.6, depth + 2.2).clone();
  sill.translate(0, -(h / 2 + pad + 0.8), 1);
  parts.push(sill);

  // Just in front of where the glass lands, so the bars read as dividing the
  // pane rather than sitting behind it.
  const barZ = depth / 2 + proud + 0.2;
  const across = new THREE.PlaneGeometry(w, bar);
  across.translate(0, 0, barZ);
  parts.push(across);
  const upright = new THREE.PlaneGeometry(bar, h);
  upright.translate(0, 0, barZ);
  parts.push(upright);

  // A rounded box arrives non-indexed and a plane indexed, and mergeGeometries
  // refuses a mixture. Drop them all to non-indexed rather than the other way
  // round: these are a handful of triangles each, and the index would cost more
  // than it saves.
  const geo =
    mergeGeometries(
      parts.map((g) => (g.index ? g.toNonIndexed() : g)),
      false,
    ) ?? surround;
  frameCache.set(key, geo);
  return geo;
}

/** A single non-instanced window — for the handful that aren't on a facade. */
function addWindow(
  scene: THREE.Object3D,
  at: { x: number; y: number; z: number; ry: number; w: number; h: number; lit: boolean },
): void {
  const depth = BUILDINGS.WINDOW_SURROUND_DEPTH;
  const nx = Math.sin(at.ry);
  const nz = Math.cos(at.ry);

  const frame = new THREE.Mesh(frameGeometry(at.w, at.h), clayMaterial(BUILDINGS.WINDOW_SILL_COLOR));
  frame.rotation.y = at.ry;
  frame.position.set(at.x + nx * (depth / 2), at.y, at.z + nz * (depth / 2));
  frame.castShadow = true;
  scene.add(frame);

  const out = depth + BUILDINGS.WINDOW_PROUD;
  const pane = new THREE.Mesh(new THREE.PlaneGeometry(at.w, at.h), glassMaterial(at.lit));
  pane.rotation.y = at.ry;
  pane.position.set(at.x + nx * out, at.y, at.z + nz * out);
  scene.add(pane);
}

/** One thing to place on a facade: where it sits and which way it faces. */
interface Placement {
  x: number;
  y: number;
  z: number;
  /** Yaw so the element's front points out of the wall. */
  ry: number;
}

/** A placement that also carries the colour of the building it belongs to. */
interface Painted extends Placement {
  paint: number;
}

/** Every facade element in the town, gathered before anything is built. */
interface Facades {
  lit: Placement[];
  unlit: Placement[];
  shop: Placement[];
  frame: Placement[];
  shopFrame: Placement[];
  shutters: Painted[];
  boxes: Placement[];
}

/**
 * Work out every window, surround, shutter and window box in the town.
 *
 * All **four** faces get windows. Only doing the two long sides left obvious
 * blank walls, because this camera angle shows you two faces of a building at
 * once almost all the time — and now that a plot is a row of narrow cottages
 * rather than one block, the "sides" of the middle one are party walls buried
 * in its neighbours, which costs nothing to skip and nothing to draw.
 *
 * Two things are deliberately reserved rather than filled: the door's slot on
 * the ground floor of the front face (windows used to be drawn straight over the
 * door), and the band below `SHOPFRONT_HEIGHT` on upper rows.
 *
 * A cottage is narrow enough that `facadeColumns` usually gives it **one**
 * window per face on the front, which is the whole point: one door and one
 * window per house, stacked, is what a terrace looks like. The old blocks got
 * three or four across and read as apartments.
 *
 * Shutters and window boxes are **front-face only**. That's partly cost — they'd
 * quadruple otherwise — but mostly restraint: dressing every elevation turns a
 * terrace into a pattern, where dressing the street elevation reads as a house
 * that gets looked at from the front.
 */
function collectFacades(cottages: Cottage[]): Facades {
  const out: Facades = {
    lit: [],
    unlit: [],
    shop: [],
    frame: [],
    shopFrame: [],
    shutters: [],
    boxes: [],
  };
  const {
    STOREY_HEIGHT: storey,
    WINDOW_SPACING: spacing,
    WINDOW_W: winW,
    WINDOW_H: winH,
    SHOPFRONT_HEIGHT: band,
    FACE_MARGIN: margin,
    WINDOW_SURROUND_DEPTH: depth,
    WINDOW_SURROUND_PAD: pad,
    WINDOW_PROUD: proud,
    SHUTTER_W: shutterW,
    SHUTTER_GAP: shutterGap,
  } = BUILDINGS;

  for (const cottage of cottages) {
    const b = cottage.spec;
    const { cx, cz } = cottage;

    for (const face of FACES) {
      // A party wall is buried in the cottage next door. It's the side with no
      // roof overhang — the same fact that stops the eaves colliding — and
      // glazing it would be a few hundred instances of window nobody can see.
      if (isPartyWall(cottage, face)) continue;

      // Along-face width, and how far out the wall sits from the centre.
      const alongX = face.nz !== 0;
      const faceW = alongX ? b.w : b.h;
      const out0 = (alongX ? b.h : b.w) / 2;
      const cols = facadeColumns(faceW, spacing, margin, winW);
      const isFront = face.facing === cottage.front;
      // The upper storey oversails front and back, so windows in it stand that
      // much further out than the ground floor's do. Miss this and every
      // first-floor window on a jettied wall is sunk inside the brickwork.
      const jetty = alongX && b.h >= b.w ? BUILDINGS.JETTY : 0;
      const at = (along: number, y: number, push: number): Placement => ({
        x: cx + (alongX ? along : face.nx * (out0 + push)),
        y,
        z: cz + (alongX ? face.nz * (out0 + push) : along),
        ry: face.ry,
      });

      // The street face's ground floor is laid out around the door rather than
      // on the column grid — a cottage is narrow, and what a window there has
      // to clear is the architrave next to it, not the next column along. See
      // terrace.ts → frontPlan.
      if (isFront) {
        for (const along of cottage.plan.windows) {
          out.frame.push(at(along, band * 0.56, depth / 2));
          out.lit.push(at(along, band * 0.56, depth + proud));
        }
      }

      for (let c = 0; c < cols; c++) {
        const along = (c - (cols - 1) / 2) * spacing;

        // --- ground floor: shop glazing on the sides and the back. The front
        // is the door's, and laid out above.
        if (!isFront) {
          out.shopFrame.push(at(along, band / 2, depth / 2));
          out.shop.push(at(along, band / 2, depth + proud));
        }

        // --- upper storeys.
        for (let r = 0; ; r++) {
          const y = band + storey * (r + 0.5);
          if (y + winH / 2 > b.tall - 6) break;
          // Deterministic pattern, so lit windows don't flicker between frames.
          const isLit =
            ((r * 7 + c * 3 + (face.ry === 0 ? 1 : 0) + Math.round(b.x)) % 100) / 100 <
            BUILDINGS.WINDOW_LIT_CHANCE;
          out.frame.push(at(along, y, jetty + depth / 2));
          (isLit ? out.lit : out.unlit).push(at(along, y, jetty + depth + proud));

          if (isFront) {
            for (const side of [-1, 1]) {
              const off = side * (winW / 2 + pad + shutterGap + shutterW / 2);
              out.shutters.push({
                ...at(along + off, y, jetty + depth / 2),
                paint: b.paint,
              });
            }
            // Window boxes on the first floor only — see the note in constants.
            if (r === 0) {
              out.boxes.push(
                at(along, y - winH / 2 - pad - 2, jetty + BUILDINGS.BOX_D / 2),
              );
            }
          }
        }
      }
    }
  }
  return out;
}

/**
 * Build the facades as **instanced** meshes — a handful of draw calls for every
 * window in the town rather than one per window. Detail here multiplies fast:
 * four faces × rows × columns, so add per-window ornament by extending a batch,
 * never by adding meshes in a loop.
 */
function buildFacades(inst: Instances, cottages: Cottage[]): void {
  const f = collectFacades(cottages);
  const {
    WINDOW_W: ww,
    WINDOW_H: wh,
    SHOP_WINDOW_W: sw,
    SHOP_WINDOW_H: sh,
    SHUTTER_W: shutterW,
    WINDOW_SURROUND_PAD: pad,
    WINDOW_SURROUND_DEPTH: depth,
    BOX_W: boxW,
    BOX_H: boxH,
    BOX_D: boxD,
    BOX_BLOOM_R: bloomR,
  } = BUILDINGS;

  const trim = clayMaterial(BUILDINGS.WINDOW_SILL_COLOR);
  const batches: Array<[Placement[], THREE.BufferGeometry, THREE.Material, string]> = [
    [f.lit, new THREE.PlaneGeometry(ww, wh), glassMaterial(true), 'lit'],
    [f.unlit, new THREE.PlaneGeometry(ww, wh), glassMaterial(false), 'unlit'],
    [f.shop, new THREE.PlaneGeometry(sw, sh), glassMaterial(true), 'shop'],
    [f.frame, frameGeometry(ww, wh), trim, 'frame'],
    [f.shopFrame, frameGeometry(sw, sh), trim, 'shopFrame'],
  ];

  for (const [placements, geo, mat, key] of batches) {
    for (const p of placements) inst.place(key, geo, mat, p.x, p.y, p.z, p.ry);
  }

  // Shutters carry their building's own paint, so they batch per colour.
  const shutterGeo = clayGeometry(shutterW, wh + pad * 2, depth);
  for (const p of f.shutters) {
    inst.place(`shutter:${p.paint}`, shutterGeo, clayMaterial(p.paint), p.x, p.y, p.z, p.ry);
  }

  // Window boxes: a terracotta trough with a few blooms sitting in it.
  const boxGeo = clayGeometry(boxW, boxH, boxD);
  const boxMat = clayMaterial(BUILDINGS.BOX_COLOR);
  const bloomGeo = claySphereGeometry(bloomR, 0.6);
  const leafGeo = claySphereGeometry(bloomR * 1.15, 0.6);
  const blooms = BUILDINGS.BOX_BLOOMS;
  for (const p of f.boxes) {
    inst.place('box', boxGeo, boxMat, p.x, p.y, p.z, p.ry);
    // Along the trough: a leafy clump in the middle, a bloom either side.
    // "Along the wall" is the face's own left-right axis, which is the face
    // normal turned a right angle — not the world x/z the placement was
    // built from.
    const ax = Math.cos(p.ry);
    const az = -Math.sin(p.ry);
    for (let i = -1; i <= 1; i++) {
      const x = p.x + ax * i * (boxW / 3.4);
      const z = p.z + az * i * (boxW / 3.4);
      const y = p.y + boxH / 2;
      if (i === 0) {
        inst.place('bloomLeaf', leafGeo, clayMaterial(BUILDINGS.BOX_LEAF_COLOR), x, y, z);
      } else {
        const tint = blooms[(i + 1) % blooms.length];
        inst.place(`bloom:${tint}`, bloomGeo, clayMaterial(tint), x, y, z);
      }
    }
  }
}
